/**
 * ComparePage — Side-by-side product comparison with show-diff toggle
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCompare } from '@contexts/CompareContext';
import { getProductByItemCode } from '@services/dataService';
import { getPDPFieldConfig } from '@data/index';
import HarveyBall from '@components/product/HarveyBall';
import Breadcrumbs from '@components/layout/Breadcrumbs';

export default function ComparePage() {
  const navigate = useNavigate();
  const { compareList, removeFromCompare } = useCompare();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDiff, setShowDiff] = useState(false);

  // Load full product data for each item in compareList
  useEffect(() => {
    let cancelled = false;

    async function loadProducts() {
      setLoading(true);
      try {
        const results = await Promise.all(
          compareList.map((item) => getProductByItemCode(item.itemCode))
        );
        if (!cancelled) {
          setProducts(results.filter(Boolean));
        }
      } catch (err) {
        console.error('Failed to load compare products:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    if (compareList.length >= 2) {
      loadProducts();
    } else {
      setProducts([]);
      setLoading(false);
    }

    return () => { cancelled = true; };
  }, [compareList]);

  // Merge field configs from all products' categories
  const mergedFields = useMemo(() => {
    const seen = new Set();
    const fields = [];
    products.forEach((p) => {
      const config = getPDPFieldConfig(p.subcategory, p.productLine);
      config.forEach((f) => {
        if (!seen.has(f.key)) {
          seen.add(f.key);
          fields.push(f);
        }
      });
    });
    return fields;
  }, [products]);

  // Empty state — fewer than 2 products selected
  if (compareList.length < 2) {
    return (
      <div className="sandvik-container py-6 text-center">
        <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-4">
          Compare products
        </h1>
        <p className="text-n5 font-sandvik-text mb-4">
          Select at least 2 products to compare.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="text-electric-blue hover:underline font-sandvik-text text-sm"
        >
          &larr; Back to browsing
        </button>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="sandvik-container py-6">
        <Breadcrumbs />
        <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-4">
          Compare products
        </h1>
        <p className="text-n5 font-sandvik-text">Loading comparison...</p>
      </div>
    );
  }

  const gridCols = `200px repeat(${products.length}, 1fr)`;

  return (
    <div className="sandvik-container py-6">
      <Breadcrumbs />

      {/* Header area */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="font-sandvik-headline font-normal text-2xl text-carbon-black mb-1">
            Compare products
          </h1>
          <button
            onClick={() => navigate(-1)}
            className="text-electric-blue hover:underline font-sandvik-text text-sm"
          >
            &larr; Back to browsing
          </button>
        </div>

        {/* Show diff toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <div
            className={`relative w-10 h-6 rounded-full transition-colors ${
              showDiff ? 'bg-electric-blue' : 'bg-n3'
            }`}
            onClick={() => setShowDiff((v) => !v)}
            role="switch"
            aria-checked={showDiff}
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setShowDiff((v) => !v);
              }
            }}
          >
            <div
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
                showDiff ? 'translate-x-4' : ''
              }`}
            />
          </div>
          <span className="text-sm text-n7 font-sandvik-text">Show differences</span>
        </label>
      </div>

      {/* Comparison table — horizontally scrollable on mobile */}
      <div className="overflow-x-auto">
        <div className="min-w-[600px]">
          {/* Header row — product cards */}
          <div className="grid" style={{ gridTemplateColumns: gridCols }}>
            <div className="p-4 font-normal text-n6 text-sm font-sandvik-text">
              Specification
            </div>
            {products.map((p) => (
              <div
                key={p.itemCode}
                className="p-4 text-center border-l border-n2"
              >
                <div className="w-20 h-20 mx-auto bg-n1 rounded flex items-center justify-center overflow-hidden">
                  {(p.images?.[0]?.src || p.imageUrl) ? (
                    <img
                      src={p.images?.[0]?.src || p.imageUrl}
                      alt={p.description}
                      className="w-full h-full object-contain p-1"
                      loading="lazy"
                    />
                  ) : (
                    <span className="text-n5 text-xs">No image</span>
                  )}
                </div>
                <p className="text-carbon-black text-sm mt-2 line-clamp-2">
                  {p.description}
                </p>
                <p className="text-xs text-n6">{p.itemCode}</p>
                <button
                  onClick={() => navigate(`/products/detail/${p.itemCode}`)}
                  className="text-xs text-electric-blue hover:underline mt-1"
                >
                  View product &rsaquo;
                </button>
                <button
                  onClick={() => removeFromCompare(p.itemCode)}
                  className="text-xs text-n5 hover:text-carbon-black mt-1 block mx-auto"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>

          {/* Spec rows */}
          {mergedFields.map((field) => (
            <div
              key={field.key}
              className="grid border-t border-n2"
              style={{ gridTemplateColumns: gridCols }}
            >
              <div className="p-3 text-sm text-n7 font-sandvik-text bg-n1">
                {field.label}
              </div>
              {products.map((p) => {
                const val = p[field.key];
                const isDiff =
                  showDiff &&
                  products.some((other) => other[field.key] !== val);

                return (
                  <div
                    key={p.itemCode}
                    className={`p-3 text-sm text-carbon-black font-sandvik-text border-l border-n2 ${
                      isDiff ? 'bg-amber-50 font-normal' : ''
                    }`}
                  >
                    {field.isHarveyBall ? (
                      <HarveyBall value={Number(val)} />
                    ) : val ? (
                      `${val}${field.unit ? ` ${field.unit}` : ''}`
                    ) : (
                      '—'
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
